import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { useDispatch } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { get_user_info, searchUsersRequest } from '../../../helpers/api';
import useDebounce from '../../../hooks/useDebounce';
import Loader from '../../common/Loader';
import CloseSharpIcon from '@material-ui/icons/CloseSharp';
import ShowResultSearch from '../../common/ShowResultSearch';
import List from '@material-ui/core/List';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { TextField, MenuItem } from '@mui/material';
import { IconButton } from '@material-ui/core';
import Button from '@mui/material/Button';
import { getDepartments } from './api';
import { useTranslation } from '../../../i18n/LanguageProvider';

const Search = () => {
  const { t } = useTranslation();
  const [departmentId, setDepartmentId] = useState('all');
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const { data: departments } = useQuery(['departments'], getDepartments, {
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const { isLoading, data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['users', searchTerm, departmentId],
      ({ pageParam = 1 }) =>
        searchUsersRequest(
          pageParam,
          searchTerm,
          departmentId !== 'all' && departmentId
        ),
      {
        select: React.useCallback(
          (data) => data?.pages.flatMap((user) => user.data),
          []
        ),
        enabled:
          (debouncedSearchTerm && searchTerm.length >= 2) || searchTerm === '',
        getNextPageParam: (lastPage, allPages) => {
          const nextPage = allPages.length + 1;
          return lastPage.data.length !== 0 ? nextPage : undefined;
        },
        onError(err) {
          toast.error(err.response?.data?.detail);
        }
      }
    );

  useEffect(() => {
    setHeader(t('nav.search'));
    setHeaderConfigs({ credits: false, exit: false, back: false, logo: false });
  }, []);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{t('nav.search')}</title>
      </Helmet>
      <div className={styles.section}>
        {isDesktop && (
          <div className={styles.pageHeader}>{t('nav.search')}</div>
        )}
        <div className={styles.sectionBody}>
          <div className={styles.search}>
            <TextField
              id="search"
              variant="filled"
              placeholder={t('search.placeholder')}
              hiddenLabel
              fullWidth
              InputProps={{
                endAdornment:
                  searchTerm !== '' ? (
                    <IconButton onClick={() => setSearchTerm('')}>
                      <CloseSharpIcon />
                    </IconButton>
                  ) : null,
                disableUnderline: true
              }}
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
            />

            <TextField
              select
              label={t('search.department')}
              fullWidth
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <MenuItem value={'all'}>{t('common.allDepartments')}</MenuItem>
              {departments?.map((dep) => (
                <MenuItem key={dep.id} value={dep.id}>
                  {dep.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <List className={styles.list}>
            {isLoading ? (
              <Loader size={40} />
            ) : data.length !== 0 ? (
              data
                .filter((u) => u.id !== get_user_info().id)
                .map((user, index) => (
                  <ShowResultSearch key={index} user={user} />
                ))
            ) : (
              <div className={styles.empty}>{t('search.noResult')}</div>
            )}
          </List>

          {!isLoading &&
            (isFetchingNextPage ? (
              <Loader size={40} />
            ) : (
              hasNextPage && (
                <Button fullWidth variant="outlined" onClick={fetchNextPage}>
                  {t('common.more')}
                </Button>
              )
            ))}
        </div>
      </div>
    </div>
  );
};

export default Search;
